// ── 모바일 결제 그룹 + 날짜순 뷰 컴포넌트 ────────────────────────────────────
// PayGroup: 미연결 결제 목록 (탭 선택 + 드래그)
// TimelineView: 날짜 내림차순 전체 뷰
// TimelineItem: 날짜순 뷰 개별 행

import React, { useState } from 'react';
import { C, TYPE_CFG } from './constants';
import { fmt, fmtDate } from './utils';
import { s } from './mobileStyles';
import { buildTimeline } from './buildTimeline';
import { LoadingPlaceholder, EmptyState } from './CommonComponents';
import { CopyTrackingButton } from '../../../components/settlement/CopyTrackingButton';
import { ConsultantChargeDetailModal } from '../../../components/settlement/ConsultantChargeDetailModal';
import { useIsDark } from '../../../hooks/useDarkMode';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import {
  stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalCloseBtn,
} from '../../admin/adminlayout/designStandard';

// ── PayGroup ──────────────────────────────────────────────────────────────────
/** 미연결 결제 그룹 (카드승인 / 전화승인 / 입금) */
export function PayGroup({
  title, type, items, selectedPay,
  onDragStart, onClick, getDate, getLabel, getSub, getAmt,
}) {
  if (!items.length) return null;
  return (
    <div style={s.payGroup}>
      <div style={s.payGroupHeader}>
        <span style={s.payGroupTitle}>{title}</span>
        <span style={s.payGroupCount}>{items.length}건</span>
      </div>
      {items.map((item) => {
        const isSel = selectedPay?.type === type && selectedPay?.id === item.id;
        return (
          <div
            key={item.id}
            draggable
            onDragStart={(e) => onDragStart(e, { type, id: item.id })}
            onClick={() => onClick({ type, id: item.id })}
            style={{ ...s.payItem, ...(isSel ? s.payItemSel : {}) }}
          >
            {/* 선택 인디케이터 */}
            <div style={{
              ...s.paySelDot,
              background: isSel ? C.accent : 'transparent',
              borderColor: isSel ? C.accent : C.border,
            }} />
            <div style={s.payContent}>
              <div style={s.payTopRow}>
                <span style={s.payDate}>{fmtDate(getDate(item))}</span>
                <span style={s.payLabel}>{getLabel(item) || '—'}</span>
                {getSub && getSub(item) && <span style={s.paySub}>{getSub(item)}</span>}
              </div>
              <div style={s.payAmt}>{fmt(getAmt(item))}원</div>
            </div>
            {/* 드래그 핸들 */}
            <div style={s.dragHandle} title="드래그하여 연결">
              <svg width="14" height="14" viewBox="0 0 24 24" fill={C.sub}>
                <circle cx="9"  cy="5"  r="1.5"/><circle cx="15" cy="5"  r="1.5"/>
                <circle cx="9"  cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
                <circle cx="9"  cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/>
              </svg>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── TimelineView ──────────────────────────────────────────────────────────────
/** 모든 항목을 날짜 내림차순으로 나열하는 뷰 (조회 전용) */
export function TimelineView({ clients, unmatched, records = [], cancelEvents = [], shareIncome, loading, consultantChargeSummaryScope = 'settlement' }) {
  if (loading) return <LoadingPlaceholder />;

  // cancelEvents: 계약 취소/복원 이벤트 배열 — 날짜순 타임라인에 함께 표시
  // shareIncome: 상담자 배분 수입 — 타 마케터 매장에서 내 상담자가 발생시킨 수입
  const items = buildTimeline(clients, unmatched, records, cancelEvents, shareIncome);

  if (items.length === 0) {
    return <EmptyState icon="📋" text="항목이 없습니다" sub="계약·결제 데이터가 없습니다" />;
  }

  return (
    <div style={s.tlWrap}>
      {items.map((item) => <TimelineItem key={item.key} item={item} consultantChargeSummaryScope={consultantChargeSummaryScope} />)}
    </div>
  );
}

// ── TimelineItem ──────────────────────────────────────────────────────────────
/** 날짜순 뷰 개별 행 */
export function TimelineItem({ item, consultantChargeSummaryScope = 'settlement' }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const isDark  = useIsDark();
  const overlayCloseProps = useOverlayClickClose(() => setDetailOpen(false));
  const tc      = TYPE_CFG[item.type] || TYPE_CFG.others;
  const isPlus  = item.amount > 0;
  const isMinus = item.amount < 0;

  // 정산지급: 다크모드일 때 불투명도 높여 눈에 띄게
  const settlementBg     = isDark ? 'rgba(13,148,136,0.25)' : 'rgba(13,148,136,0.08)';
  const settlementBorder = isDark ? 'rgba(13,148,136,0.55)' : 'rgba(13,148,136,0.25)';
  // 계약취소 이벤트: 연한 빨간 배경, 계약복원: 연한 초록 배경 — 상태 변화 시각적 강조
  const cancelBg     = isDark ? 'rgba(220,38,38,0.13)'  : 'rgba(220,38,38,0.05)';
  const cancelBorder = isDark ? 'rgba(220,38,38,0.40)'  : 'rgba(220,38,38,0.18)';
  const restoreBg     = isDark ? 'rgba(5,150,105,0.13)' : 'rgba(5,150,105,0.05)';
  const restoreBorder = isDark ? 'rgba(5,150,105,0.40)' : 'rgba(5,150,105,0.18)';

  // 타입별 행 배경/테두리 결정
  const rowBg = item.type === 'settlement' ? settlementBg
    : item.type === 'cancel'               ? cancelBg
    : item.type === 'restore'              ? restoreBg
    : undefined;
  const rowBorder = item.type === 'settlement' ? `1px solid ${settlementBorder}`
    : item.type === 'cancel'                   ? `1px solid ${cancelBorder}`
    : item.type === 'restore'                  ? `1px solid ${restoreBorder}`
    : undefined;
  const contractChangeEvents = Array.isArray(item.contractChangeEvents) ? item.contractChangeEvents : [];
  const hasContractChangeEvents = contractChangeEvents.length > 1;
  const detailButton = item.type === 'consultant_charge' && item.detail ? (
    <button
      type="button"
      onClick={() => setDetailOpen(true)}
      style={{
        height: 28,
        minWidth: 76,
        border: '1px solid rgba(220,38,38,0.22)',
        borderRadius: 6,
        background: 'rgba(220,38,38,0.07)',
        color: '#dc2626',
        fontSize: 11,
        fontWeight: 900,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        padding: '0 12px',
        flexShrink: 0,
      }}
    >
      계산내역
    </button>
  ) : null;
  const contractChangeButton = hasContractChangeEvents ? (
    <button
      type="button"
      onClick={() => setDetailOpen(true)}
      style={{
        height: 24,
        minWidth: 48,
        border: `1px solid ${item.type === 'cancel' ? 'rgba(220,38,38,0.25)' : 'rgba(5,150,105,0.25)'}`,
        borderRadius: 6,
        background: item.type === 'cancel' ? 'rgba(220,38,38,0.07)' : 'rgba(5,150,105,0.07)',
        color: item.type === 'cancel' ? '#dc2626' : '#059669',
        fontSize: 11,
        fontWeight: 900,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        padding: '0 10px',
        flexShrink: 0,
      }}
    >
      상세
    </button>
  ) : null;

  return (
    <div style={{
      ...s.tlRow,
      background: rowBg,
      border:     rowBorder,
    }}>
      {/* 날짜 + 구분 뱃지 — 세로 배치, 고정 너비로 날짜와 칩 폭 통일 */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, flexShrink: 0, width: 46 }}>
        <span style={s.tlDate}>{item.date ? fmtDate(item.date) : '—'}</span>
        <span style={{
          ...s.tlBadge, background: tc.bg, color: tc.color,
          fontSize: 9, padding: '1px 4px',
          display: 'block', textAlign: 'center', width: '100%', boxSizing: 'border-box',
        }}>{tc.label}</span>
      </div>

      {/* 내용 — 2행 고정: 모든 카드 동일 높이 유지
          1행: 매장명 / 미연결 / (정산지급은 빈 공간)
          2행: 라벨 + 송장복사 버튼 or 부가정보 */}
      <div style={s.tlContent}>
        {/* 1행: 매장명 또는 미연결 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, minHeight: 16, overflow: 'hidden' }}>
          {item.type !== 'settlement' && (
            item.store
              ? <>
                  {/* 매장명 + 계약취소 칩 — flexShrink:1로 매장명이 줄어들며 칩이 바로 옆에 붙음 */}
                  <span style={{ ...s.tlStore, flexShrink: 1, minWidth: 0 }}>{item.store}</span>
                  {/* 계약취소 배지 — is_cancelled=true인 계약에만 표시 */}
                  {item.isCancelled && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: 'rgba(220,38,38,0.12)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.3)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                      계약취소
                    </span>
                  )}
                </>
              // matched:false인 항목만 "미연결" 표시 — 기타·상담자배분수입 등 매장 없는 항목은 제외
              : !item.matched ? <span style={s.tlUnmatched}>미연결</span> : null
          )}
        </div>
        {/* 2행: delivery는 버튼만 / 나머지는 라벨 + 버튼 or 부가정보
            minHeight: 20 — 송장복사 버튼(~20px)이 잘리지 않도록 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, minHeight: 20, overflow: 'hidden' }}>
          {item.type === 'delivery' && item.trackingText
            ? <CopyTrackingButton text={item.trackingText} />
            : <>
                {/* label이 null이면 렌더 안 함 (정산지급 메모 없는 경우 — 배지 중복 방지) */}
                {item.label && <span style={{ ...s.tlLabel, flexShrink: 1, minWidth: 0 }}>{item.label}</span>}
                {detailButton}
                {contractChangeButton}
                {item.trackingText
                  ? <CopyTrackingButton text={item.trackingText} />
                  : item.sub
                    ? <span style={s.tlSub}>{item.sub}</span>
                    : null
                }
              </>
          }
        </div>
      </div>

      {/* 원금 */}
      {item.origAmt != null && <span style={s.tlOrigAmt}>{fmt(item.origAmt)}</span>}

      {/* 금액 (commission) */}
      <span style={{
        ...s.tlAmt,
        color: isPlus ? C.plus : isMinus ? C.minus : C.subDark,
      }}>
        {isPlus ? '+' : ''}{fmt(item.amount)}
      </span>
      {detailOpen && item.type === 'consultant_charge' && (
        <ConsultantChargeDetailModal
          charge={item.detail}
          summaryScope={consultantChargeSummaryScope}
          onClose={() => setDetailOpen(false)}
        />
      )}
      {detailOpen && hasContractChangeEvents && (
        <div
          {...overlayCloseProps}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 3000,
            background: 'rgba(15,23,42,0.36)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(420px, 100%)',
              maxHeight: '70vh',
              border: '1px solid var(--border)',
              borderRadius: 10,
              background: 'var(--bg-card)',
              boxShadow: '0 18px 50px rgba(15,23,42,0.25)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* 모달 헤더 — 검정 표준 밴드 (2026-08-31 모달 통일) */}
            <div style={stdModalHeaderBar(10)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <div style={stdModalHeaderIconBox} aria-hidden="true">📋</div>
                <div style={stdModalHeaderTitle}>계약 상태 변경 상세</div>
              </div>
              <button
                type="button"
                onClick={() => setDetailOpen(false)}
                aria-label="닫기"
                style={stdModalCloseBtn}
              >
                ✕
              </button>
            </div>
            <div style={{ overflowY: 'auto', padding: 14 }}>
              <div style={{ display: 'grid', gap: 8 }}>
                {contractChangeEvents.map((ev) => (
                  <div
                    key={ev.id}
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      padding: '9px 10px',
                      background: 'var(--bg-soft)',
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 900, color: ev.action === 'cancel' ? '#dc2626' : '#059669' }}>
                      {ev.eventTime || '-'} · {ev.direction}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.45 }}>
                      {ev.actorName ? `${ev.actorName} 처리` : '처리자 미기록'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
